import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem, Chip, ToggleButtonGroup, ToggleButton, Button, IconButton, Tooltip as MuiTooltip, Autocomplete, TextField } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { colors as TAColors } from '../../themes/technicalAlerts';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';
import { Table, ConfigProvider, Empty } from 'antd';
import esES from 'antd/locale/es_ES';
import enUS from 'antd/locale/en_US';
import itIT from 'antd/locale/it_IT';
import 'antd/dist/reset.css';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import POSAIAnalysisPanel from '../../components/ProblemAnalysisPanel/POSAIAnalysisPanel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ProductAIActionPopover from '../../components/POSAnalysis/ProductAIActionPopover';
import AggregatedExperimentsModal from '../../components/POSAnalysis/AggregatedExperimentsModal';

// Spanish fallbacks for the cluster cadence seasonal note (keyed by backend seasonalNoteCode)
const SEASONAL_NOTE_DEFAULTS = {
  verano_pico_outdoor: 'Pico de verano activo: demanda típicamente alta',
  verano_descenso_indoor: 'Descenso esperado en verano para sectores de interior',
  pre_verano_aumento: 'Aumento esperado en verano',
  post_verano_descenso: 'Descenso esperado post-verano',
  post_verano_recuperacion: 'Recuperación esperada post-verano',
};

// Cuerpo de las comparativas del chip: más pequeño que el caption (12px) para que
// "↓ -32.3% vs mismo período año anterior" quepa entero en una línea.
const BADGE_FONT_SIZE = '10px';

// Delta badge component with colored percentage and arrow
const DeltaBadge = ({ value, t, comparisonKey = 'monthToDateComparisonHint' }) => {
  if (value === null || value === undefined) {
    return <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: BADGE_FONT_SIZE }}>—</Typography>;
  }
  const color = value > 0 ? TAColors.success : value < 0 ? TAColors.error : TAColors.text.muted;
  const arrow = value > 0 ? '↑' : value < 0 ? '↓' : '→';
  const formatted = `${value.toFixed(1)}%`;
  const comparison = t(comparisonKey);
  // display:flex (no inline-flex) para que cada comparativa ocupe su propia línea: si van
  // en la misma fila el ancho mínimo del chip crece y empuja la página hacia la derecha.
  // Y una sola línea SIEMPRE: cuerpo 10px, sin wrap y elipsis cuando el chip se queda corto
  // (pantalla estrecha o etiqueta larga en it/en); el `title` enseña el texto completo.
  return (
    <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', mt: 0.5, color, gap: 0.5, width: '100%', minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontSize: BADGE_FONT_SIZE, lineHeight: 1.4, flexShrink: 0 }}>{arrow}</Typography>
      <Typography variant="caption" sx={{ fontSize: BADGE_FONT_SIZE, lineHeight: 1.4, fontWeight: 600, flexShrink: 0 }}>{formatted}</Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        title={comparison}
        sx={{ fontSize: BADGE_FONT_SIZE, lineHeight: 1.4, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {comparison}
      </Typography>
    </Box>
  );
};

export const POSAnalysisTab = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, isAuthenticated } = useAuth();
  const jsLocale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';
  const currentMonthStart = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  }, []);
  const [monthCursor, setMonthCursor] = useState(() => new Date(currentMonthStart));
  const [periodMode, setPeriodMode] = useState('month'); // 'month' | 'quarter'
  const selectedMonthNumber = monthCursor.getMonth() + 1;
  const selectedYearNumber = monthCursor.getFullYear();
  const selectedQuarterNumber = Math.floor((selectedMonthNumber - 1) / 3) + 1;
  const currentQuarterNumber = Math.floor(currentMonthStart.getMonth() / 3) + 1;
  const monthName = useMemo(() => {
    const fallback = new Intl.DateTimeFormat(jsLocale, { month: 'long' }).format(monthCursor);
    return t(`month_names.${selectedMonthNumber}`, { defaultValue: fallback });
  }, [jsLocale, monthCursor, selectedMonthNumber, t]);
  const monthLabel = useMemo(
    () => {
      if (periodMode === 'quarter') {
        const quarterLabel = t(`quarter_names.${selectedQuarterNumber}`, {
          defaultValue: `Q${selectedQuarterNumber}`
        });
        return `${quarterLabel} ${selectedYearNumber}`;
      }
      return `${monthName} ${selectedYearNumber}`;
    },
    [monthName, periodMode, selectedQuarterNumber, selectedYearNumber, t]
  );
  const isCurrentSelectedMonth =
    monthCursor.getFullYear() === currentMonthStart.getFullYear() &&
    monthCursor.getMonth() === currentMonthStart.getMonth();
  const isCurrentSelectedQuarter =
    selectedYearNumber === currentMonthStart.getFullYear() &&
    selectedQuarterNumber === currentQuarterNumber;
  const isCurrentSelectedPeriod = periodMode === 'quarter' ? isCurrentSelectedQuarter : isCurrentSelectedMonth;
  const handlePrevMonth = () => {
    setMonthCursor((prev) => {
      const delta = periodMode === 'quarter' ? 3 : 1;
      const nextDate = new Date(prev.getFullYear(), prev.getMonth() - delta, 1);
      if (periodMode === 'quarter') {
        const alignedMonth = Math.floor(nextDate.getMonth() / 3) * 3;
        return new Date(nextDate.getFullYear(), alignedMonth, 1);
      }
      return nextDate;
    });
  };
  const handleNextMonth = () => {
    setMonthCursor((prev) => {
      const latestAllowed =
        periodMode === 'quarter'
          ? new Date(currentMonthStart.getFullYear(), (currentQuarterNumber - 1) * 3, 1)
          : currentMonthStart;
      const delta = periodMode === 'quarter' ? 3 : 1;
      const candidate = new Date(prev.getFullYear(), prev.getMonth() + delta, 1);
      const alignedCandidate =
        periodMode === 'quarter'
          ? new Date(candidate.getFullYear(), Math.floor(candidate.getMonth() / 3) * 3, 1)
          : candidate;
      if (alignedCandidate > latestAllowed) {
        return prev;
      }
      return alignedCandidate;
    });
  };
  const handlePeriodModeChange = (_, value) => {
    if (!value || value === periodMode) return;
    setPeriodMode(value);
    if (value === 'quarter') {
      setMonthCursor((prev) => new Date(prev.getFullYear(), Math.floor(prev.getMonth() / 3) * 3, 1));
    }
  };
  const monthQueryParams = useMemo(
    () => `&month=${selectedMonthNumber}&year=${selectedYearNumber}`,
    [selectedMonthNumber, selectedYearNumber]
  );
  const clusterPeriodParams = useMemo(() => {
    if (periodMode === 'quarter') {
      return `&periodType=quarter&quarter=${selectedQuarterNumber}&year=${selectedYearNumber}`;
    }
    return `&periodType=month&month=${selectedMonthNumber}&year=${selectedYearNumber}`;
  }, [periodMode, selectedMonthNumber, selectedQuarterNumber, selectedYearNumber]);
  const periodLabel = periodMode === 'quarter' ? t('period_this_quarter') : t('period_this_month');
  const comparisonTranslationKey = periodMode === 'quarter' ? 'quarterToDateComparisonHint' : 'monthToDateComparisonHint';
  
  // Handle opening AI analysis panel
  const handleOpenAIAnalysis = () => {
    let selectedName = '';
    let selectedId = '';
    if (analysisMode === 'machine') {
      selectedName = selectedMachineName;
      selectedId = selectedMachine;
    } else if (analysisMode === 'cluster') {
      selectedName = selectedClusterName;
      selectedId = selectedCluster;
    } else if (analysisMode === 'sector') {
      selectedName = selectedSectorName;
      selectedId = selectedSector;
    } else {
      selectedName = selectedRouteName;
      selectedId = selectedRoute;
    }

    window.currentPageSidebar = {
      content: (
        <POSAIAnalysisPanel
          analysisMode={analysisMode}
          selectedCluster={selectedClusterName}
          selectedSector={selectedSectorName}
          selectedRoute={selectedRouteName}
          selectedMachine={selectedMachineName}
          storeClusterId={selectedCluster}
          sectorId={selectedSector}
          routeId={selectedRoute}
          machineId={selectedMachine}
          selectedNameOverride={selectedName}
          selectedIdOverride={selectedId}
          customerId={currentUser?.customer_id}
          referencePeriod={referencePeriod}
          onClose={() => {
            window.currentPageSidebar = null;
            window.dispatchEvent(new Event('sidebarUpdate'));
          }}
          t={t}
        />
      ),
      overlayMode: true
    };
    
    // Notify Layout to show sidebar
    window.dispatchEvent(new Event('sidebarUpdate'));
  };

  const handleOpenProductAiAction = useCallback((event, record) => {
    event.stopPropagation();
    if (!record?.aiAction) return;
    setAiActionAnchorEl(event.currentTarget);
    setSelectedAiActionRow(record);
  }, []);

  const handleCloseProductAiAction = useCallback(() => {
    setAiActionAnchorEl(null);
    setSelectedAiActionRow(null);
  }, []);

  const handleAiActionUpdated = useCallback(() => {
    // Close popover first to prevent stale anchor reference causing top-left positioning
    handleCloseProductAiAction();
    setProductsRefreshKey((prev) => prev + 1);
  }, [handleCloseProductAiAction]);

  // Handlers for cluster experiments modal
  const handleOpenClusterProblemsModal = useCallback((event, record) => {
    event.stopPropagation();
    setClusterModalProduct(record);
    setClusterModalOpen(true);
  }, []);

  const handleCloseClusterModal = useCallback(() => {
    setClusterModalOpen(false);
    setClusterModalProduct(null);
  }, []);

  const handleClusterExperimentsUpdated = useCallback(() => {
    setProductsRefreshKey((prev) => prev + 1);
  }, []);

  const handleToggleProblemSort = useCallback((event) => {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }
    // Clear column sort when activating problem sort (mutually exclusive)
    setSortBy(null);
    setSortOrder(null);
    setProblemSortDirection((prev) => {
      if (!prev) return 'desc';
      if (prev === 'desc') return 'asc';
      return null;
    });
  }, []);

  // State for store clusters
  const [clustersLoading, setClustersLoading] = useState(true);
  const [clustersError, setClustersError] = useState(null);
  const [storeClusters, setStoreClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  // Machine mode state
  const [storeMachines, setStoreMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [machinesLoading, setMachinesLoading] = useState(false);
  const [machinesError, setMachinesError] = useState(null);
  // Sector mode state
  const [analysisMode, setAnalysisMode] = useState('cluster'); // 'machine' | 'cluster' | 'sector' | 'route'
  const [storeSectors, setStoreSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [sectorsError, setSectorsError] = useState(null);
  // Route mode state
  const [storeRoutes, setStoreRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState('');
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routesError, setRoutesError] = useState(null);
  const [routesInitialized, setRoutesInitialized] = useState(false);
  
  // State for current month metrics
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [currentMonthMetrics, setCurrentMonthMetrics] = useState(null);
  const [additionalMetrics, setAdditionalMetrics] = useState(null);
  const [weeklyEvolution, setWeeklyEvolution] = useState([]);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalProducts, setTotalProducts] = useState(0);

  // State for product fetch
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [problemSortDirection, setProblemSortDirection] = useState(null); // Default: pure profit sort (no problem priority)

  // State for column sort + subcategory filter (Phase 4)
  const [sortBy, setSortBy] = useState(null); // 'units' | 'revenue' | 'profit' | 'price' | 'cost' | 'share' | null
  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null

  // Handle column header click for sorting (mutually exclusive with problemSort).
  // Reads current sort state directly — event handlers are not double-invoked by
  // StrictMode, so the desc -> asc -> clear cycle advances exactly one step per click.
  const handleColumnSort = useCallback((columnKey) => {
    setProblemSortDirection(null);
    if (sortBy === columnKey) {
      if (sortOrder === 'desc') {
        setSortOrder('asc');
      } else if (sortOrder === 'asc') {
        setSortBy(null);
        setSortOrder(null);
      } else {
        setSortOrder('desc');
      }
    } else {
      setSortBy(columnKey);
      setSortOrder('desc');
    }
  }, [sortBy, sortOrder]);
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [subcategories, setSubcategories] = useState([]);

  // AI action popover state
  const [aiActionAnchorEl, setAiActionAnchorEl] = useState(null);
  const [selectedAiActionRow, setSelectedAiActionRow] = useState(null);

  // Aggregated experiments modal state (for cluster mode)
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const [clusterModalProduct, setClusterModalProduct] = useState(null);

  // State for weekly evolution chart
  const [chartIndicator, setChartIndicator] = useState('units');
  const [chartData, setChartData] = useState([]);
  // no collapsible trend; always visible in compact area

  // State for member machines panel (cluster/route drill-down)
  const [memberMachines, setMemberMachines] = useState([]);
  const [memberMachinesLoading, setMemberMachinesLoading] = useState(false);
  const [memberMachinesError, setMemberMachinesError] = useState(null);
  const [memberMachinesPanelExpanded, setMemberMachinesPanelExpanded] = useState(false);
  // Track which entity triggered the drill-in so we can return to it
  const [drillInSource, setDrillInSource] = useState(null); // { mode: 'cluster'|'route', id, name }

  // State for cadence recommendation (cluster mode only)
  const [cadenceRecommendation, setCadenceRecommendation] = useState(null);
  

  // Fetch store clusters with problem counts on component mount
  useEffect(() => {
    const fetchStoreClusters = async () => {
      if (!isAuthenticated || !currentUser?.customer_id) return;
      setClustersLoading(true);
      setClustersError(null);
      try {
        const token = localStorage.getItem('access_token');
        const resp = await axios.get(
          `${API_BASE_URL}/store-clusters-options?clientId=${encodeURIComponent(currentUser.customer_id)}`,
          { headers: createAuthHeader(token) }
        );
        setStoreClusters(resp.data.clusters || []);
        // Don't auto-select any cluster - let user choose
      } catch (e) {
        setClustersError(e.message || t('failed_load_store_clusters'));
      } finally {
        setClustersLoading(false);
      }
    };
    fetchStoreClusters();
  }, [currentUser, isAuthenticated, t]);

  useEffect(() => {
    const fetchMachines = async () => {
      if (analysisMode !== 'machine') {
        return;
      }
      if (!isAuthenticated || !currentUser?.customer_id) {
        return;
      }
      setMachinesLoading(true);
      setMachinesError(null);
      try {
        const token = localStorage.getItem('access_token');
        const resp = await axios.get(
          `${API_BASE_URL}/machine-options?clientId=${encodeURIComponent(currentUser.customer_id)}`,
          { headers: createAuthHeader(token) }
        );
        setStoreMachines(resp.data?.machines || []);
      } catch (e) {
        setMachinesError(e.message || t('failed_load_store_clusters'));
        setStoreMachines([]);
      } finally {
        setMachinesLoading(false);
      }
    };
    fetchMachines();
  }, [analysisMode, currentUser, isAuthenticated, t]);

  // Route mode now supports problem sort - removed the reset

  // Fetch summary metrics when selection or month changes
  useEffect(() => {
    const fetchSummary = async () => {
      const hasSelection = analysisMode === 'machine'
        ? !!selectedMachine
        : analysisMode === 'cluster'
          ? !!selectedCluster
          : analysisMode === 'sector'
            ? !!selectedSector
            : !!selectedRoute;
      if (!isAuthenticated || !currentUser?.customer_id || !hasSelection) {
        setCurrentMonthMetrics(null);
        setAdditionalMetrics(null);
        setWeeklyEvolution([]);
        setSummaryError(null);
        setSummaryLoading(false);
        return;
      }
      setSummaryLoading(true);
      setSummaryError(null);
      try {
        const token = localStorage.getItem('access_token');
        const id = analysisMode === 'machine'
          ? selectedMachine
          : analysisMode === 'cluster'
            ? selectedCluster
            : analysisMode === 'sector'
              ? selectedSector
              : selectedRoute;
        const encodedId = encodeURIComponent(id);
        const url = analysisMode === 'machine'
          ? `${API_BASE_URL}/metrics/store/${encodedId}/summary?clientId=${encodeURIComponent(currentUser.customer_id)}${clusterPeriodParams}`
          : analysisMode === 'cluster'
            ? `${API_BASE_URL}/metrics/store-cluster/${encodedId}/summary?clientId=${encodeURIComponent(currentUser.customer_id)}${clusterPeriodParams}`
            : analysisMode === 'sector'
              ? `${API_BASE_URL}/metrics/sector/${encodedId}/summary?clientId=${encodeURIComponent(currentUser.customer_id)}${clusterPeriodParams}`
              : `${API_BASE_URL}/metrics/route/${encodedId}/summary?clientId=${encodeURIComponent(currentUser.customer_id)}${clusterPeriodParams}`;
        const resp = await axios.get(url, { headers: createAuthHeader(token) });
        const data = resp.data;
        if (data && data.currentMonth) {
          const cm = data.currentMonth;
          const normalized = {
            year: cm.year ?? null,
            month: cm.month ?? null,
            quarter: cm.quarter ?? null,
            periodType: cm.periodType ?? 'month',
            units: Number(cm.totalUnits ?? 0),
            revenue: Number(cm.totalRevenue ?? 0),
            profit: Number(cm.totalProfit ?? 0),
            deltas: {
              unitsPct: cm?.deltas?.unitsPct ?? null,
              revenuePct: cm?.deltas?.revenuePct ?? null,
              profitPct: cm?.deltas?.profitPct ?? null,
              unitsYearAgoPct: cm?.deltas?.unitsYearAgoPct ?? null,
              revenueYearAgoPct: cm?.deltas?.revenueYearAgoPct ?? null,
              profitYearAgoPct: cm?.deltas?.profitYearAgoPct ?? null,
            },
          };
          setCurrentMonthMetrics(normalized);
          if (data.additionalMetrics) {
            setAdditionalMetrics({
              avgDailyUnits: Number(data.additionalMetrics.avgDailyUnits ?? 0),
              avgTicketSize: Number(data.additionalMetrics.avgTicketSize ?? 0),
              avgDailyRevenue: Number(data.additionalMetrics.avgDailyRevenue ?? 0),
            });
          } else {
            setAdditionalMetrics(null);
          }
          const weekly = Array.isArray(data.weeklyEvolution)
            ? data.weeklyEvolution.map((row) => ({
                week: Number(row.week ?? 0),
                units: Number(row.units ?? 0),
                revenue: Number(row.revenue ?? 0),
                profit: Number(row.profit ?? 0)
              }))
            : [];
          setWeeklyEvolution(weekly);
          setSummaryError(null);
        } else {
          setCurrentMonthMetrics(null);
          setAdditionalMetrics(null);
          setWeeklyEvolution([]);
        }
      } catch (e) {
        setSummaryError(e.message || t('failed_load_metrics'));
        setCurrentMonthMetrics(null);
        setAdditionalMetrics(null);
        setWeeklyEvolution([]);
      } finally {
        setSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [
    currentUser,
    isAuthenticated,
    analysisMode,
    selectedMachine,
    selectedCluster,
    selectedSector,
    selectedRoute,
    t,
    selectedMonthNumber,
    selectedYearNumber,
    clusterPeriodParams,
    monthQueryParams
  ]);

  // Fetch paginated products for the table
  useEffect(() => {
    const fetchProducts = async () => {
      const hasSelection = analysisMode === 'machine'
        ? !!selectedMachine
        : analysisMode === 'cluster'
          ? !!selectedCluster
          : analysisMode === 'sector'
            ? !!selectedSector
            : !!selectedRoute;
      if (!isAuthenticated || !currentUser?.customer_id || !hasSelection) {
        setProducts([]);
        setTotalProducts(0);
        setSubcategories([]);
        setProductsError(null);
        setProductsLoading(false);
        return;
      }
      setProductsLoading(true);
      setProductsError(null);
      try {
        const token = localStorage.getItem('access_token');
        const id = analysisMode === 'machine'
          ? selectedMachine
          : analysisMode === 'cluster'
            ? selectedCluster
            : analysisMode === 'sector'
              ? selectedSector
              : selectedRoute;
        const encodedId = encodeURIComponent(id);
        const problemSortQuery =
          (analysisMode === 'machine' || analysisMode === 'cluster' || analysisMode === 'sector' || analysisMode === 'route') && problemSortDirection
            ? `&problemSort=${problemSortDirection}`
            : '';
        // Column sort query params (mutually exclusive with problemSort)
        const columnSortQuery = sortBy && sortOrder
          ? `&sortBy=${sortBy}&sortOrder=${sortOrder}`
          : '';
        // Subcategory filter
        const subcategoryQuery = subcategoryFilter
          ? `&subcategoryFilter=${encodeURIComponent(subcategoryFilter)}`
          : '';
        const url = analysisMode === 'machine'
          ? `${API_BASE_URL}/metrics/store/${encodedId}/products?clientId=${encodeURIComponent(currentUser.customer_id)}&page=${page}${clusterPeriodParams}${problemSortQuery}${columnSortQuery}${subcategoryQuery}`
          : analysisMode === 'cluster'
            ? `${API_BASE_URL}/metrics/store-cluster/${encodedId}/products?clientId=${encodeURIComponent(currentUser.customer_id)}&page=${page}${clusterPeriodParams}${problemSortQuery}${columnSortQuery}${subcategoryQuery}`
            : analysisMode === 'sector'
              ? `${API_BASE_URL}/metrics/sector/${encodedId}/products?clientId=${encodeURIComponent(currentUser.customer_id)}&page=${page}${clusterPeriodParams}${problemSortQuery}${columnSortQuery}${subcategoryQuery}`
              : `${API_BASE_URL}/metrics/route/${encodedId}/products?clientId=${encodeURIComponent(currentUser.customer_id)}&page=${page}${clusterPeriodParams}${problemSortQuery}${columnSortQuery}${subcategoryQuery}`;
        const resp = await axios.get(url, { headers: createAuthHeader(token) });
        const data = resp.data;
        if (data && Array.isArray(data.products)) {
          setProducts(data.products);
          setTotalProducts(Number(data.productsTotal || 0));
          // Parse subcategories from response (Phase 4)
          if (Array.isArray(data.subcategories)) {
            setSubcategories(data.subcategories.filter(s => s && s.trim() !== '').sort());
          } else {
            setSubcategories([]);
          }
        } else {
          setProducts([]);
          setTotalProducts(0);
          setSubcategories([]);
        }
      } catch (e) {
        setProductsError(e.message || t('failed_load_metrics'));
        setProducts([]);
        setTotalProducts(0);
        setSubcategories([]);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, [
    currentUser,
    isAuthenticated,
    analysisMode,
    selectedMachine,
    selectedCluster,
    selectedSector,
    selectedRoute,
    page,
    t,
    selectedMonthNumber,
    selectedYearNumber,
    clusterPeriodParams,
    monthQueryParams,
    productsRefreshKey,
    problemSortDirection,
    sortBy,
    sortOrder,
    subcategoryFilter
  ]);

  // Reset page when selection or mode changes
  useEffect(() => {
    setPage(1);
  }, [selectedCluster, selectedSector, selectedRoute, selectedMachine, analysisMode, selectedMonthNumber, selectedYearNumber, periodMode, problemSortDirection, sortBy, sortOrder, subcategoryFilter]);

  useEffect(() => {
    setProblemSortDirection(null);
    setSortBy(null);
    setSortOrder(null);
    setSubcategoryFilter('');
  }, [selectedMonthNumber, selectedYearNumber, periodMode]);

  useEffect(() => {
    if (!aiActionAnchorEl) return;
    if (analysisMode !== 'machine') {
      handleCloseProductAiAction();
      return;
    }
    if (!selectedMachine) {
      handleCloseProductAiAction();
    }
  }, [analysisMode, selectedMachine, aiActionAnchorEl, handleCloseProductAiAction]);

  // Client-side filters and sorting removed by request

  // Derive chart data from the cached weekly evolution
  useEffect(() => {
    if (!weeklyEvolution.length) {
      setChartData([]);
      return;
    }
    const rows = weeklyEvolution.map((row) => ({
      week: Number(row.week ?? 0),
      value: Number(row[chartIndicator] ?? 0)
    }));
    setChartData(rows);
  }, [weeklyEvolution, chartIndicator]);

  const chartValueFormatter = useMemo(() => {
    const toK = (v) => {
      const n = Number(v) || 0;
      if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}K`;
      return `${Math.round(n)}`;
    };
    if (chartIndicator === 'revenue' || chartIndicator === 'profit') {
      return (v) => `€${toK(v)}`;
    }
    return (v) => toK(v);
  }, [chartIndicator]);

  // Full value formatter for tooltip (no K compaction)
  const chartTooltipValueFormatter = useMemo(() => {
    if (chartIndicator === 'revenue' || chartIndicator === 'profit') {
      return (v) => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(v) || 0);
    }
    return (v) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(v) || 0);
  }, [chartIndicator]);

  // X-axis ticks: show every 3 weeks to reduce clutter, always include first and last
  const chartXTicks = useMemo(() => {
    const weeks = Array.from(new Set(chartData.map(d => Number(d.week)).filter(Boolean))).sort((a, b) => a - b);
    if (weeks.length === 0) return [];
    const selected = weeks.filter(w => w % 3 === 0);
    if (!selected.includes(weeks[0])) selected.unshift(weeks[0]);
    const last = weeks[weeks.length - 1];
    if (!selected.includes(last)) selected.push(last);
    return selected;
  }, [chartData]);

  const getMonthText = useCallback((monthNumber, yearNumber) => {
    if (!monthNumber || !yearNumber) return null;
    const date = new Date(yearNumber, monthNumber - 1, 1);
    const fallback = new Intl.DateTimeFormat(jsLocale, { month: 'long' }).format(date);
    const name = t(`month_names.${monthNumber}`, { defaultValue: fallback });
    return `${name} ${yearNumber}`;
  }, [jsLocale, t]);

  const metricsMonthLabel = useMemo(() => {
    if (!currentMonthMetrics) return null;
    if (currentMonthMetrics.periodType === 'quarter' && currentMonthMetrics.quarter && currentMonthMetrics.year) {
      const quarterLabel = t(`quarter_names.${currentMonthMetrics.quarter}`, {
        defaultValue: `Q${currentMonthMetrics.quarter}`
      });
      return `${quarterLabel} ${currentMonthMetrics.year}`;
    }
    if (currentMonthMetrics.month && currentMonthMetrics.year) {
      return getMonthText(currentMonthMetrics.month, currentMonthMetrics.year);
    }
    return null;
  }, [currentMonthMetrics, getMonthText, t]);
  const referencePeriod = useMemo(() => {
    if (periodMode === 'quarter') {
      return {
        periodType: 'quarter',
        quarter: selectedQuarterNumber,
        year: selectedYearNumber
      };
    }
    return {
      periodType: 'month',
      month: selectedMonthNumber,
      year: selectedYearNumber
    };
  }, [periodMode, selectedMonthNumber, selectedQuarterNumber, selectedYearNumber]);

  const selectedMachineName = useMemo(() => {
    const machine = storeMachines.find((m) => String(m.id) === String(selectedMachine));
    if (!machine) {
      return selectedMachine;
    }
    return machine.displayName || machine.alias || machine.storeId || String(machine.id);
  }, [selectedMachine, storeMachines]);

  const selectedClusterName = useMemo(() => {
    const cluster = storeClusters.find(c => c.id === selectedCluster);
    return cluster ? cluster.name : selectedCluster;
  }, [selectedCluster, storeClusters]);
  const selectedSectorName = useMemo(() => {
    const sector = storeSectors.find(s => s.id === selectedSector);
    return sector ? sector.name : selectedSector;
  }, [selectedSector, storeSectors]);
  const selectedRouteName = useMemo(() => {
    const route = storeRoutes.find(r => String(r.id) === String(selectedRoute));
    return route ? route.name : selectedRoute;
  }, [selectedRoute, storeRoutes]);

  // Ready flag to render the analysis content (cluster or sector)
  const ready = useMemo(() => {
    if (analysisMode === 'machine') {
      return !machinesLoading && !machinesError && !!selectedMachine;
    }
    if (analysisMode === 'cluster') {
      return !clustersLoading && !clustersError && !!selectedCluster;
    }
    if (analysisMode === 'sector') {
      return !sectorsLoading && !sectorsError && !!selectedSector;
    }
    return !routesError && !!selectedRoute;
  }, [analysisMode, clustersLoading, clustersError, machinesLoading, machinesError, sectorsLoading, sectorsError, routesError, selectedCluster, selectedSector, selectedRoute, selectedMachine]);

  // Load sectors when switching to sector mode
  useEffect(() => {
    const fetchSectors = async () => {
      if (!isAuthenticated || !currentUser?.customer_id || analysisMode !== 'sector') return;
      setSectorsLoading(true);
      setSectorsError(null);
      try {
        const token = localStorage.getItem('access_token');
        const resp = await axios.get(
          `${API_BASE_URL}/sectors-options?clientId=${encodeURIComponent(currentUser.customer_id)}`,
          { headers: createAuthHeader(token) }
        );
        setStoreSectors(resp.data.sectors || []);
      } catch (e) {
        setSectorsError(e.message || t('failed_load_store_clusters'));
      } finally {
        setSectorsLoading(false);
      }
    };
    fetchSectors();
  }, [analysisMode, currentUser, isAuthenticated, t]);

  useEffect(() => {
    setRoutesInitialized(false);
    setStoreRoutes([]);
    setSelectedRoute('');
  }, [analysisMode, currentUser?.customer_id]);

  useEffect(() => {
    if (analysisMode !== 'route') {
      setStoreRoutes([]);
      setSelectedRoute('');
      setRoutesError(null);
      setRoutesLoading(false);
      return;
    }
    const fetchRoutes = async () => {
      if (!isAuthenticated || !currentUser?.customer_id) {
        return;
      }
      if (routesInitialized) {
        return;
      }
      setRoutesLoading(true);
      setRoutesError(null);
      try {
        const token = localStorage.getItem('access_token');
        const resp = await axios.get(
          `${API_BASE_URL}/routes-options?clientId=${encodeURIComponent(currentUser.customer_id)}&month=${selectedMonthNumber}&year=${selectedYearNumber}`,
          { headers: createAuthHeader(token) }
        );
        const routes = resp.data.routes || [];
        setStoreRoutes(routes);
        if (selectedRoute && !routes.some((route) => String(route.id) === String(selectedRoute))) {
          setSelectedRoute('');
        }
        setRoutesInitialized(true);
      } catch (e) {
        setRoutesError(e.message || t('failed_load_store_clusters'));
        setStoreRoutes([]);
        setRoutesInitialized(false);
      } finally {
        setRoutesLoading(false);
      }
    };
    fetchRoutes();
  }, [analysisMode, currentUser, isAuthenticated, routesInitialized, t, selectedMonthNumber, selectedYearNumber]);

  useEffect(() => {
    if (analysisMode !== 'route') return;
    if (!selectedRoute) return;
    if (!storeRoutes.length) return;
    const stillExists = storeRoutes.some((route) => String(route.id) === String(selectedRoute));
    if (!stillExists) {
      setSelectedRoute('');
    }
  }, [analysisMode, storeRoutes, selectedRoute]);

  useEffect(() => {
    if (analysisMode !== 'machine') return;
    if (!selectedMachine) return;
    if (!storeMachines.length) return;
    const stillExists = storeMachines.some((machine) => String(machine.id) === String(selectedMachine));
    if (!stillExists) {
      setSelectedMachine('');
    }
  }, [analysisMode, storeMachines, selectedMachine]);

  // Fetch member machines when in cluster or route mode with a selection
  useEffect(() => {
    const fetchMemberMachines = async () => {
      // Only fetch for cluster or route modes
      if (analysisMode !== 'cluster' && analysisMode !== 'route') {
        setMemberMachines([]);
        setMemberMachinesError(null);
        setMemberMachinesLoading(false);
        return;
      }

      const entityId = analysisMode === 'cluster' ? selectedCluster : selectedRoute;
      if (!isAuthenticated || !currentUser?.customer_id || !entityId) {
        setMemberMachines([]);
        setMemberMachinesError(null);
        setMemberMachinesLoading(false);
        return;
      }

      setMemberMachinesLoading(true);
      setMemberMachinesError(null);
      try {
        const token = localStorage.getItem('access_token');
        const encodedId = encodeURIComponent(entityId);
        const url = analysisMode === 'cluster'
          ? `${API_BASE_URL}/store-clusters/${encodedId}/machines?clientId=${encodeURIComponent(currentUser.customer_id)}`
          : `${API_BASE_URL}/routes/${encodedId}/machines?clientId=${encodeURIComponent(currentUser.customer_id)}`;
        const resp = await axios.get(url, { headers: createAuthHeader(token) });
        setMemberMachines(resp.data?.machines || []);
      } catch (e) {
        setMemberMachinesError(e.message || t('failed_load_store_clusters'));
        setMemberMachines([]);
      } finally {
        setMemberMachinesLoading(false);
      }
    };
    fetchMemberMachines();
  }, [analysisMode, selectedCluster, selectedRoute, currentUser, isAuthenticated, t]);

  // Fetch cadence recommendation when in cluster mode with a selection
  useEffect(() => {
    const fetchCadenceRecommendation = async () => {
      if (analysisMode !== 'cluster' || !selectedCluster || !isAuthenticated || !currentUser?.customer_id) {
        setCadenceRecommendation(null);
        return;
      }
      try {
        const token = localStorage.getItem('access_token');
        const encodedId = encodeURIComponent(selectedCluster);
        const resp = await axios.get(
          `${API_BASE_URL}/metrics/store-cluster/${encodedId}/cadence-recommendation?clientId=${encodeURIComponent(currentUser.customer_id)}`,
          { headers: createAuthHeader(token) }
        );
        setCadenceRecommendation(resp.data || null);
      } catch {
        setCadenceRecommendation(null);
      }
    };
    fetchCadenceRecommendation();
  }, [analysisMode, selectedCluster, currentUser, isAuthenticated]);

  // Handler to drill into a specific machine from the member machines panel
  const handleDrillIntoMachine = useCallback((machineId, machineDisplayName) => {
    // Remember where we came from so we can return
    if (analysisMode === 'cluster') {
      setDrillInSource({ mode: 'cluster', id: selectedCluster, name: selectedClusterName });
    } else if (analysisMode === 'route') {
      setDrillInSource({ mode: 'route', id: selectedRoute, name: selectedRouteName });
    }
    setSelectedMachine(machineId);
    setAnalysisMode('machine');
  }, [analysisMode, selectedCluster, selectedClusterName, selectedRoute, selectedRouteName]);

  // Handler to return to the previous cluster/route view after drilling into a machine
  const handleReturnFromDrillIn = useCallback(() => {
    if (!drillInSource) return;
    setAnalysisMode(drillInSource.mode);
    setDrillInSource(null);
  }, [drillInSource]);

  // Clear drill-in source when user manually switches modes (not via drill-in)
  useEffect(() => {
    // If we're no longer in machine mode and there was a drill-in source, clear it
    if (analysisMode !== 'machine' && drillInSource) {
      setDrillInSource(null);
    }
  }, [analysisMode, drillInSource]);

  // Cleanup sidebar on component unmount
  useEffect(() => {
    return () => {
      window.currentPageSidebar = null;
      window.dispatchEvent(new Event('sidebarUpdate'));
    };
  }, []);

  return (
    <Box sx={{ 
      backgroundColor: 'transparent',
      minHeight: 'auto',
      p: 2
    }}>
      <Paper elevation={3} sx={{ p: 2, borderRadius: '12px', backgroundColor: 'white' }}>
        {/* Back link when drilled into machine from cluster/route */}
        {analysisMode === 'machine' && drillInSource && (
          <Box
            onClick={handleReturnFromDrillIn}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1,
              cursor: 'pointer',
              color: '#6B46C1',
              '&:hover': {
                color: '#553C9A',
                textDecoration: 'underline',
              }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {drillInSource.mode === 'cluster'
                ? t('back_to_cluster', { name: drillInSource.name, defaultValue: `Volver a Ubicacion ${drillInSource.name}` })
                : t('back_to_route', { name: drillInSource.name, defaultValue: `Volver a Ruta ${drillInSource.name}` })
              }
            </Typography>
          </Box>
        )}

        {/* Header */}
        <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Row 1: Title */}
          <Typography sx={{ fontSize: '18px', fontWeight: 600, color: 'text.secondary' }}>
            {analysisMode === 'sector'
              ? 'Análisis de Sector'
              : analysisMode === 'route'
                ? t('pos_analysis_route_title', { defaultValue: 'Análisis de Ruta' })
                : analysisMode === 'machine'
                  ? t('pos_analysis_machine_title', { defaultValue: 'Análisis de Máquina' })
                  : t('pos_analysis_title')}
          </Typography>

          {/* Row 2: Controls */}
          <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, flexWrap: 'wrap', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' } }}>
            {/* Left side: Mode toggle and Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flexDirection: { xs: 'column', sm: 'row' } }}>
              {/* Mode toggle */}
              <ToggleButtonGroup
              size="small"
              exclusive
              value={analysisMode}
              onChange={(e, val) => { if (val) setAnalysisMode(val); }}
              aria-label="analysis mode"
              sx={{ width: { xs: '100%', sm: 'auto' }, '& .MuiToggleButton-root': { flex: { xs: 1, sm: 'none' }, px: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.62rem', sm: '0.8125rem' }, whiteSpace: 'nowrap' } }}
            >
              <ToggleButton value="machine" aria-label="machine">
                Máquina
              </ToggleButton>
              <ToggleButton value="cluster" aria-label="cluster">
                Ubicación
              </ToggleButton>
              <ToggleButton value="sector" aria-label="sector">
                Sector
              </ToggleButton>
              <ToggleButton value="route" aria-label="route">
                Ruta
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Store Cluster / Sector Selector inline next to title */}
          {analysisMode === 'machine' ? (
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 }, maxWidth: '100%', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
              value={storeMachines.find(m => m.id === selectedMachine) || null}
              onChange={(event, newValue) => setSelectedMachine(newValue?.id || '')}
              options={storeMachines}
              getOptionLabel={(option) => option?.displayName || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={machinesLoading}
              renderInput={(params) => {
                const selectedOpt = storeMachines.find(m => m.id === selectedMachine);
                const problemCount = Number(selectedOpt?.problemCount ?? 0);
                const alertCount = Number(selectedOpt?.alertCount ?? 0);
                return (
                  <TextField
                    {...params}
                    label={t('machine', { defaultValue: 'Máquina' })}
                    placeholder={t('select_machine_option', { defaultValue: 'Seleccionar máquina' })}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Box sx={{ display: 'flex', gap: 0.5, mr: 0.5 }}>
                            {problemCount > 0 && (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  px: 0.75,
                                  height: 20,
                                  borderRadius: '999px',
                                  backgroundColor: '#fee2e2',
                                  color: '#b91c1c',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {problemCount}
                              </Box>
                            )}
                            {alertCount > 0 && (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  px: 0.75,
                                  height: 20,
                                  borderRadius: '999px',
                                  backgroundColor: '#f3e8ff',
                                  color: '#7c3aed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {alertCount}
                              </Box>
                            )}
                          </Box>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                const problemCount = Number(option.problemCount ?? 0);
                const alertCount = Number(option.alertCount ?? 0);
                return (
                  <li {...props} key={option.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1.5,
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
                        {option.displayName}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {problemCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {problemCount}
                          </Box>
                        )}
                        {alertCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {alertCount}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
            />
          ) : analysisMode === 'cluster' ? (
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 }, maxWidth: '100%', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
              value={storeClusters.find(c => c.id === selectedCluster) || null}
              onChange={(event, newValue) => setSelectedCluster(newValue?.id || '')}
              options={storeClusters}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={clustersLoading}
              renderInput={(params) => {
                const selectedOpt = storeClusters.find(c => c.id === selectedCluster);
                const problemCount = Number(selectedOpt?.problemCount ?? 0);
                const alertCount = Number(selectedOpt?.alertCount ?? 0);
                return (
                  <TextField
                    {...params}
                    label={t('store_cluster')}
                    placeholder={t('select_store_cluster_option')}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {(problemCount > 0 || alertCount > 0) && (
                            <MuiTooltip title={t('open_ai_diagnostics', { defaultValue: 'Ver diagnóstico IA' })}>
                              <Box
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenClusterProblemsModal(e, {
                                    id: null,
                                    name: null,
                                    aiAction: { problemCount, alertCount }
                                  });
                                }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: 'pointer',
                                  '&:hover': { opacity: 0.8 }
                                }}
                              >
                                {problemCount > 0 && (
                                  <Box
                                    sx={{
                                      minWidth: 24,
                                      px: 0.75,
                                      height: 20,
                                      borderRadius: '999px',
                                      backgroundColor: '#fee2e2',
                                      color: '#b91c1c',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {problemCount}
                                  </Box>
                                )}
                                {alertCount > 0 && (
                                  <Box
                                    sx={{
                                      minWidth: 24,
                                      px: 0.75,
                                      height: 20,
                                      borderRadius: '999px',
                                      backgroundColor: '#f3e8ff',
                                      color: '#7c3aed',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {alertCount}
                                  </Box>
                                )}
                              </Box>
                            </MuiTooltip>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                const problemCount = Number(option.problemCount ?? 0);
                const alertCount = Number(option.alertCount ?? 0);
                return (
                  <li {...props} key={option.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1.5,
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
                        {option.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {problemCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {problemCount}
                          </Box>
                        )}
                        {alertCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {alertCount}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
            />
          ) : analysisMode === 'sector' ? (
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 }, maxWidth: '100%', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
              value={storeSectors.find(s => s.id === selectedSector) || null}
              onChange={(event, newValue) => setSelectedSector(newValue?.id || '')}
              options={storeSectors}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={sectorsLoading}
              renderInput={(params) => {
                const selectedOpt = storeSectors.find(s => s.id === selectedSector);
                const problemCount = Number(selectedOpt?.problemCount ?? 0);
                const alertCount = Number(selectedOpt?.alertCount ?? 0);
                return (
                  <TextField
                    {...params}
                    label={'Sector'}
                    placeholder={'Seleccionar sector'}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {problemCount > 0 && (
                            <Box
                              sx={{
                                minWidth: 24,
                                px: 0.75,
                                height: 20,
                                borderRadius: '999px',
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                mr: 0.5,
                              }}
                            >
                              {problemCount}
                            </Box>
                          )}
                          {alertCount > 0 && (
                            <Box
                              sx={{
                                minWidth: 24,
                                px: 0.75,
                                height: 20,
                                borderRadius: '999px',
                                backgroundColor: '#f3e8ff',
                                color: '#7c3aed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                mr: 0.5,
                              }}
                            >
                              {alertCount}
                            </Box>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                const problemCount = Number(option.problemCount ?? 0);
                const alertCount = Number(option.alertCount ?? 0);
                return (
                  <li {...props} key={option.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1.5,
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
                        {option.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {problemCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {problemCount}
                          </Box>
                        )}
                        {alertCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {alertCount}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
            />
          ) : (
            <Autocomplete
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 }, maxWidth: '100%', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
              value={storeRoutes.find(r => r.id === selectedRoute) || null}
              onChange={(event, newValue) => setSelectedRoute(newValue?.id || '')}
              options={storeRoutes}
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              disabled={routesLoading}
              renderInput={(params) => {
                const selectedOpt = storeRoutes.find(r => r.id === selectedRoute);
                const problemCount = Number(selectedOpt?.problemCount ?? 0);
                const alertCount = Number(selectedOpt?.alertCount ?? 0);
                return (
                  <TextField
                    {...params}
                    label={t('store_route', { defaultValue: 'Ruta' })}
                    placeholder={t('select_store_route_option', { defaultValue: 'Seleccionar ruta' })}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {problemCount > 0 && (
                            <Box
                              sx={{
                                minWidth: 24,
                                px: 0.75,
                                height: 20,
                                borderRadius: '999px',
                                backgroundColor: '#fee2e2',
                                color: '#b91c1c',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                mr: 0.5,
                              }}
                            >
                              {problemCount}
                            </Box>
                          )}
                          {alertCount > 0 && (
                            <Box
                              sx={{
                                minWidth: 24,
                                px: 0.75,
                                height: 20,
                                borderRadius: '999px',
                                backgroundColor: '#f3e8ff',
                                color: '#7c3aed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                mr: 0.5,
                              }}
                            >
                              {alertCount}
                            </Box>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                );
              }}
              renderOption={(props, option) => {
                const problemCount = Number(option.problemCount ?? 0);
                const alertCount = Number(option.alertCount ?? 0);
                return (
                  <li {...props} key={option.id}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        gap: 1.5,
                      }}
                    >
                      <Typography component="span" sx={{ fontSize: '0.9rem', color: 'text.primary' }}>
                        {option.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {problemCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#fee2e2',
                              color: '#b91c1c',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {problemCount}
                          </Box>
                        )}
                        {alertCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 24,
                              px: 0.75,
                              height: 20,
                              borderRadius: '999px',
                              backgroundColor: '#f3e8ff',
                              color: '#7c3aed',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {alertCount}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
            />
          )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                  px: 1,
                  py: 0.25,
                  backgroundColor: 'rgba(15, 23, 42, 0.03)'
                }}
              >
                <IconButton
                  size="small"
                  onClick={handlePrevMonth}
                  aria-label={t('previous_month')}
                  sx={{ p: 0.5 }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    color: 'text.secondary',
                    minWidth: 90,
                    textAlign: 'center'
                  }}
                >
                  {monthLabel}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleNextMonth}
                  aria-label={t('next_month')}
                  disabled={isCurrentSelectedPeriod}
                  sx={{ p: 0.5 }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              {(['machine', 'cluster', 'sector', 'route'].includes(analysisMode)) && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={periodMode}
                  onChange={handlePeriodModeChange}
                  aria-label={t('pos_analysis_period_toggle', { defaultValue: 'Periodo' })}
                >
                  <ToggleButton value="month" aria-label="month" sx={{ fontSize: '0.75rem', px: 1 }}>
                    {t('pos_analysis_period_month', { defaultValue: 'Mes' })}
                  </ToggleButton>
                  <ToggleButton value="quarter" aria-label="quarter" sx={{ fontSize: '0.75rem', px: 1 }}>
                    {t('pos_analysis_period_quarter', { defaultValue: 'Trimestre' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
          </Box>

            {/* Right side: AI Button */}
            <Button
              variant="outlined"
              onClick={handleOpenAIAnalysis}
              startIcon={<AutoAwesomeIcon sx={{ fontSize: '16px' }} />}
              disabled={!ready}
              sx={{
                background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
                borderColor: 'rgba(107, 70, 193, 0.2)',
                color: '#6B46C1',
                fontSize: '13px',
                fontWeight: 600,
                py: 0.75,
                px: 2,
                borderRadius: '8px',
                textTransform: 'none',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                  borderColor: 'rgba(107, 70, 193, 0.3)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(107, 70, 193, 0.15)'
                },
                '&:active': {
                  transform: 'translateY(0px)'
                }
              }}
            >
              {t('pos_analysis_analyze_month', { month: monthLabel })}
            </Button>
          </Box>
        </Box>

        {/* Loading/Error states for selections */}
        {machinesLoading && analysisMode==='machine' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.disabled">{t('loading')}</Typography>
          </Box>
        )}

        {machinesError && analysisMode==='machine' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="error.main">{t('error')}: {machinesError}</Typography>
          </Box>
        )}

        {/* Loading/Error states for clusters */}
        {clustersLoading && analysisMode==='cluster' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.disabled">{t('loading')}</Typography>
          </Box>
        )}

        {clustersError && analysisMode==='cluster' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="error.main">{t('error')}: {clustersError}</Typography>
          </Box>
        )}

        {routesLoading && analysisMode==='route' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.disabled">{t('loading')}</Typography>
          </Box>
        )}

        {routesError && analysisMode==='route' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="error.main">{t('error')}: {routesError}</Typography>
          </Box>
        )}

        {/* No selection state (machine) */}
        {!machinesLoading && !machinesError && analysisMode==='machine' && !selectedMachine && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('select_machine_title', { defaultValue: 'Selecciona una máquina' })}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('select_machine_message', { defaultValue: 'Elige una máquina para analizar su rendimiento.' })}
            </Typography>
          </Box>
        )}

        {/* No selection state (cluster) */}
        {!clustersLoading && !clustersError && analysisMode==='cluster' && !selectedCluster && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('select_store_cluster_title')}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('select_store_cluster_message')}
            </Typography>
          </Box>
        )}

        {/* No selection state (sector) */}
        {!sectorsLoading && !sectorsError && analysisMode==='sector' && !selectedSector && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {'Selecciona un sector'}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {'Elige un sector para ver el análisis'}
            </Typography>
          </Box>
        )}

        {!routesLoading && !routesError && analysisMode==='route' && !selectedRoute && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('select_store_route_title', { defaultValue: 'Selecciona una ruta' })}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {t('select_store_route_message', { defaultValue: 'Elige una ruta para ver el análisis' })}
            </Typography>
          </Box>
        )}

        {/* Main Content - Only show if selection is made (cluster or sector) */}
        {ready && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', mt: 1 }}>
            {/* Unified header for symmetry */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.95rem' }}>{t('current_month_kpis')}</Typography>
                {!summaryLoading && !summaryError && metricsMonthLabel && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {metricsMonthLabel}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="indicator-label" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{t('indicator')}</InputLabel>
                  <Select labelId="indicator-label" value={chartIndicator} label={t('indicator')} onChange={(e) => setChartIndicator(e.target.value)} sx={{ '& .MuiSelect-select': { fontSize: '0.8rem', color: 'text.secondary', py: 0.25 } }}>
                    <MenuItem sx={{ fontSize: '0.8rem' }} value={'units'}>{t('kpi_units')}</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8rem' }} value={'revenue'}>{t('kpi_revenue')}</MenuItem>
                    <MenuItem sx={{ fontSize: '0.8rem' }} value={'profit'}>{t('kpi_profit')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Two-column content: chips left, plot right */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 2 }}>
              {/* Left: KPI chips */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {/* Row 1: Core KPIs.
                    minWidth 0 en chip y label: sin esto el ancho mínimo de la columna lo fija el
                    texto sin cortar de las comparativas y la elipsis nunca entra — la rejilla
                    crecería y volvería a empujar la página hacia la derecha. */}
                <Box sx={{
                  display: 'grid',
                  gap: 1.25,
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
                  '& .MuiChip-root': { minWidth: 0 },
                  '& .MuiChip-label': { minWidth: 0, overflow: 'hidden' },
                }}>
                  {/* Units */}
                  <Chip
                  clickable={false}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  icon={<span role="img" aria-label="units" style={{ fontSize: 16 }}>📦</span>}
                  variant="outlined"
                  sx={{
                    borderWidth: 2,
                    height: 'auto',
                    px: 1.5,
                    py: 1.25,
                    minHeight: 58,
                    width: '100%',
                    borderColor: 'rgba(0,0,0,0.2)',
                    '& .MuiChip-icon': { alignSelf: 'flex-start', mr: 0.5, position: 'relative', top: '-1px' },
                    '& .MuiChip-label': { p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box' }
                  }}
                  label={(
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                        {t('kpi_units')} {periodLabel}
                      </Typography>
                      <Typography sx={{ fontWeight: 300, fontSize: '0.95rem', color: 'text.primary' }}>
                        {new Intl.NumberFormat().format(currentMonthMetrics?.units || 0)}
                      </Typography>
                      <DeltaBadge value={currentMonthMetrics?.deltas?.unitsPct} t={t} comparisonKey={comparisonTranslationKey} />
                      <DeltaBadge value={currentMonthMetrics?.deltas?.unitsYearAgoPct} t={t} comparisonKey="yearAgoComparisonHint" />
                    </Box>
                  )}
                />
                  {/* Revenue */}
                  <Chip
                  clickable={false}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  icon={<span role="img" aria-label="revenue" style={{ fontSize: 16 }}>💶</span>}
                  variant="outlined"
                  sx={{ borderWidth: 2, height: 'auto', px: 1.5, py: 1.25, minHeight: 58, width: '100%', borderColor: 'rgba(0,0,0,0.2)', '& .MuiChip-icon': { alignSelf: 'flex-start', mr: 0.5, position: 'relative', top: '-1px' }, '& .MuiChip-label': { p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box' } }}
                  label={(
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                        {t('kpi_revenue')} {periodLabel}
                      </Typography>
                      <Typography sx={{ fontWeight: 300, fontSize: '0.95rem', color: 'text.primary' }}>
                        {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(currentMonthMetrics?.revenue || 0)}
                      </Typography>
                      <DeltaBadge value={currentMonthMetrics?.deltas?.revenuePct} t={t} comparisonKey={comparisonTranslationKey} />
                      <DeltaBadge value={currentMonthMetrics?.deltas?.revenueYearAgoPct} t={t} comparisonKey="yearAgoComparisonHint" />
                    </Box>
                  )}
                />
                  {/* Profit */}
                  <Chip
                  clickable={false}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  icon={<span role="img" aria-label="profit" style={{ fontSize: 16 }}>📈</span>}
                  variant="outlined"
                  sx={{ borderWidth: 2, height: 'auto', px: 1.5, py: 1.25, minHeight: 58, width: '100%', borderColor: 'rgba(0,0,0,0.2)', '& .MuiChip-icon': { alignSelf: 'flex-start', mr: 0.5, position: 'relative', top: '-1px' }, '& .MuiChip-label': { p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box' } }}
                  label={(
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                        {t('kpi_profit')} {periodLabel}
                      </Typography>
                      <Typography sx={{ fontWeight: 300, fontSize: '0.95rem', color: 'text.primary' }}>
                        {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(currentMonthMetrics?.profit || 0)}
                      </Typography>
                      <DeltaBadge value={currentMonthMetrics?.deltas?.profitPct} t={t} comparisonKey={comparisonTranslationKey} />
                      <DeltaBadge value={currentMonthMetrics?.deltas?.profitYearAgoPct} t={t} comparisonKey="yearAgoComparisonHint" />
                    </Box>
                  )}
                />
                </Box>
                {/* Row 2: Additional metrics */}
                <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
                  <Chip
                    clickable={false}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    icon={<span role="img" aria-label="daily-units" style={{ fontSize: 16 }}>📅</span>}
                    variant="outlined"
                    sx={{ borderWidth: 2, height: 'auto', px: 1.5, py: 1.25, minHeight: 58, width: '100%', borderColor: 'rgba(0,0,0,0.2)', '& .MuiChip-icon': { alignSelf: 'flex-start', mr: 0.5, position: 'relative', top: '-1px' }, '& .MuiChip-label': { p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box' } }}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>Promedio unidades diarias</Typography>
                        <Typography sx={{ fontWeight: 300, fontSize: '0.95rem', color: 'text.primary' }}>
                          {new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(additionalMetrics?.avgDailyUnits || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Promedio últimos 3 meses</Typography>
                      </Box>
                    )}
                  />
                  <Chip
                    clickable={false}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    icon={<span role="img" aria-label="avg-ticket" style={{ fontSize: 16 }}>🎟️</span>}
                    variant="outlined"
                    sx={{ borderWidth: 2, height: 'auto', px: 1.5, py: 1.25, minHeight: 58, width: '100%', borderColor: 'rgba(0,0,0,0.2)', '& .MuiChip-icon': { alignSelf: 'flex-start', mr: 0.5, position: 'relative', top: '-1px' }, '& .MuiChip-label': { p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box' } }}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>Ticket medio por cliente</Typography>
                        <Typography sx={{ fontWeight: 300, fontSize: '0.95rem', color: 'text.primary' }}>
                          {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(additionalMetrics?.avgTicketSize || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Promedio últimos 3 meses</Typography>
                      </Box>
                    )}
                  />
                  <Chip
                    clickable={false}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    icon={<span role="img" aria-label="daily-revenue" style={{ fontSize: 16 }}>🛍️</span>}
                    variant="outlined"
                    sx={{ borderWidth: 2, height: 'auto', px: 1.5, py: 1.25, minHeight: 58, width: '100%', borderColor: 'rgba(0,0,0,0.2)', '& .MuiChip-icon': { alignSelf: 'flex-start', mr: 0.5, position: 'relative', top: '-1px' }, '& .MuiChip-label': { p: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', width: '100%', boxSizing: 'border-box' } }}
                    label={(
                      <Box>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>Promedio ingresos diarios</Typography>
                        <Typography sx={{ fontWeight: 300, fontSize: '0.95rem', color: 'text.primary' }}>
                          {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(additionalMetrics?.avgDailyRevenue || 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Promedio últimos 3 meses</Typography>
                      </Box>
                    )}
                  />
                </Box>
                {/* Row 3: Periodicidad recomendada (cluster mode only) — esta semana → 2-4 sem + why (all scenarios) */}
                {analysisMode === 'cluster' && cadenceRecommendation && (() => {
                  const cr = cadenceRecommendation;
                  const reason = cr.reason || {};
                  const rec = cr.recommendedCadence ?? cr.currentCadence;
                  const proj = cr.projection?.cadence ?? rec;
                  // Weekly sales shown = the SAME basis as the KPI box (avg daily units x 7), so the two
                  // reconcile exactly; project it by the seasonal ratio from the endpoint.
                  const apiWeekly = reason.weeklyUnitsSold ?? cr.weeklyUnits ?? 0;
                  const apiProj = reason.projectedWeeklyUnits ?? apiWeekly;
                  const seasonalRatio = apiWeekly > 0 ? apiProj / apiWeekly : 1;
                  const kpiDaily = additionalMetrics?.avgDailyUnits || 0;
                  const units = kpiDaily > 0 ? Math.round(kpiDaily * 7) : Math.round(apiWeekly);
                  const projUnits = Math.round(units * seasonalRatio);
                  const seasonal = cr.seasonalNoteCode;
                  // Forward change direction (recommended now vs in 2-4 weeks)
                  const dir = proj > rec ? 'up' : proj < rec ? 'down' : 'none';
                  // Scenario for the why text: seasonal flavour only when it agrees with the direction, else generic trend
                  const seasonUp = seasonal === 'verano_pico_outdoor' || seasonal === 'pre_verano_aumento';
                  const seasonRecovery = seasonal === 'post_verano_recuperacion';
                  const seasonDownSummer = seasonal === 'verano_descenso_indoor';
                  const seasonDownPost = seasonal === 'post_verano_descenso';
                  const scenario = dir === 'none' ? 'stable'
                    : dir === 'up' ? (seasonUp ? 'up_summer' : seasonRecovery ? 'up_postsummer' : 'up_trend')
                    : (seasonDownSummer ? 'down_summer' : seasonDownPost ? 'down_postsummer' : 'down_trend');
                  const whyDefaults = {
                    stable: 'Vende ~{{units}} uds/sem: con {{rec}} visitas basta y se mantiene.',
                    up_summer: 'Vende ~{{units}} uds/sem. Llega el verano: subirá a ~{{projUnits}} → {{proj}} visitas/sem.',
                    up_postsummer: 'Vende ~{{units}} uds/sem. Recuperación post-verano: subirá a ~{{projUnits}} → {{proj}} visitas/sem.',
                    up_trend: 'Vende ~{{units}} uds/sem y va al alza: subirá a ~{{projUnits}} → {{proj}} visitas/sem.',
                    down_summer: 'Vende ~{{units}} uds/sem. Bajón de verano: bajará a ~{{projUnits}} → {{proj}} visitas/sem.',
                    down_postsummer: 'Vende ~{{units}} uds/sem. Bajada post-verano: bajará a ~{{projUnits}} → {{proj}} visitas/sem.',
                    down_trend: 'Vende ~{{units}} uds/sem y va a la baja: bajará a ~{{projUnits}} → {{proj}} visitas/sem.',
                  };
                  const whyText = t(`cadence_why_${scenario}`, { units, projUnits, proj, rec, defaultValue: whyDefaults[scenario] });
                  const projColor = dir === 'up' ? 'warning.main' : dir === 'down' ? 'success.main' : 'text.primary';
                  const trendSym = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
                  return (
                  <Paper variant="outlined" onMouseDown={(e) => e.stopPropagation()} sx={{ p: 1.25, borderWidth: 2, borderColor: 'rgba(0,0,0,0.2)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.75 }}>
                      <span role="img" aria-label="cadence" style={{ fontSize: 18 }}>🗓️</span>
                      {/* Esta semana */}
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>
                          {t('cadence_now_label', { defaultValue: 'Esta semana' })}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: 'text.primary' }}>{rec}</Typography>
                          <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{t('cadence_per_week', { defaultValue: 'visitas/sem' })}</Typography>
                        </Box>
                      </Box>
                      <Typography component="span" sx={{ color: 'text.disabled', fontSize: '1.2rem' }}>→</Typography>
                      {/* En 2-4 sem */}
                      <Box>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: 0.3, color: 'text.secondary', textTransform: 'uppercase' }}>
                          {t('cadence_next_label', { defaultValue: 'En 2-4 sem' })}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                          <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.05rem', color: projColor }}>{proj}</Typography>
                          <Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{t('cadence_per_week', { defaultValue: 'visitas/sem' })}</Typography>
                          <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.95rem', color: projColor }}>{trendSym}</Typography>
                        </Box>
                      </Box>
                      {/* Why as plain text (current units + scenario + projected units) */}
                      <Box sx={{ flex: 1, minWidth: 260, pl: { sm: 1.5 }, borderLeft: { sm: '1px solid' }, borderColor: { sm: 'divider' } }}>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontSize: '0.82rem', lineHeight: 1.35 }}>
                          {whyText}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                  );
                })()}
              </Box>
              {/* Right: compact trend (title centered) */}
              <Box sx={{ borderLeft: { xs: 'none', md: `1px solid ${TAColors.border.light}` }, pl: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 0.5 }}>
                  {t('weekly_evolution')}
                </Typography>
                <Box sx={{ flex: 1, minHeight: 180 }}>
                  {summaryLoading ? (
                    <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.disabled">{t('loading')}</Typography>
                    </Box>
                  ) : summaryError ? (
                    <Typography variant="body2" color="error.main">{t('error')}: {summaryError}</Typography>
                  ) : chartData.length === 0 ? (
                    <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.disabled">{t('noData')}</Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 20, left: 0 }}>
                      <defs>
                        <linearGradient id="areaPrimary" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={TAColors.primary} stopOpacity={0.15} />
                          <stop offset="100%" stopColor={TAColors.primary} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={TAColors.border.light} vertical={false} />
                      <XAxis dataKey="week" tickLine={false} axisLine={false} stroke={TAColors.text.muted} fontSize={10} ticks={chartXTicks} interval={0} label={{ value: t('week'), position: 'bottom', offset: 6, style: { fill: TAColors.text.muted, fontSize: 10, fontWeight: 600 } }} />
                      <YAxis tickLine={false} axisLine={false} stroke={TAColors.text.muted} fontSize={10} width={36} tickFormatter={chartValueFormatter} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${TAColors.border.normal}`, fontSize: 10, padding: '6px 8px' }} labelStyle={{ color: TAColors.text.muted, fontSize: 10 }} itemStyle={{ color: TAColors.text.secondary, fontSize: 10, padding: 0 }} labelFormatter={(w) => `${t('week')} ${w}`} formatter={(v) => [chartTooltipValueFormatter(v), '']} />
                      <Line type="monotone" dataKey="value" stroke={TAColors.primary} strokeWidth={2} dot={false} activeDot={{ r: 2.5 }} />
                      <Area type="monotone" dataKey="value" fill="url(#areaPrimary)" stroke="none" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Box>
            </Box>
          </Box>
          </Paper>
        )}

        {/* Member Machines Panel (for cluster/route modes) */}
        {ready && (analysisMode === 'cluster' || analysisMode === 'route') && (
          <Paper
            variant="outlined"
            sx={{
              mt: 1.25,
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            {/* Collapsible Header */}
            <Box
              onClick={() => setMemberMachinesPanelExpanded(!memberMachinesPanelExpanded)}
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: memberMachinesPanelExpanded ? 'rgba(107, 70, 193, 0.04)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(107, 70, 193, 0.06)',
                },
                transition: 'background-color 0.2s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'text.primary' }}>
                  {analysisMode === 'cluster'
                    ? t('machines_in_cluster', { defaultValue: 'Maquinas de esta ubicacion' })
                    : t('machines_in_route', { defaultValue: 'Maquinas de esta ruta' })
                  }
                </Typography>
                {!memberMachinesLoading && memberMachines.length > 0 && (
                  <Chip
                    label={memberMachines.length}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(107, 70, 193, 0.1)',
                      color: '#6B46C1',
                    }}
                  />
                )}
              </Box>
              {memberMachinesPanelExpanded ? (
                <ExpandLessIcon sx={{ color: 'text.secondary' }} />
              ) : (
                <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
              )}
            </Box>

            {/* Expandable Content */}
            {memberMachinesPanelExpanded && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                {memberMachinesLoading ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.disabled">{t('loading')}</Typography>
                  </Box>
                ) : memberMachinesError ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="error.main">{t('error')}: {memberMachinesError}</Typography>
                  </Box>
                ) : memberMachines.length === 0 ? (
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.disabled">
                      {t('no_machines_found', { defaultValue: 'No se encontraron maquinas' })}
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      maxHeight: 240,
                      overflowY: 'auto',
                    }}
                  >
                    {memberMachines.map((machine) => {
                      const problemCount = Number(machine.problemCount ?? 0);
                      const alertCount = Number(machine.alertCount ?? 0);
                      return (
                        <Box
                          key={machine.id}
                          onClick={() => handleDrillIntoMachine(machine.id, machine.displayName)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                            transition: 'background-color 0.15s',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: '#2563eb',
                              fontWeight: 500,
                            }}
                          >
                            {machine.displayName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {problemCount > 0 && (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  px: 0.75,
                                  height: 20,
                                  borderRadius: '999px',
                                  backgroundColor: '#fee2e2',
                                  color: '#b91c1c',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {problemCount}
                              </Box>
                            )}
                            {alertCount > 0 && (
                              <Box
                                sx={{
                                  minWidth: 24,
                                  px: 0.75,
                                  height: 20,
                                  borderRadius: '999px',
                                  backgroundColor: '#f3e8ff',
                                  color: '#7c3aed',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                }}
                              >
                                {alertCount}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        )}

        {/* Top Products Table */}
        {ready && (
          <Box sx={{ mt: 1.25 }}>
            <Box sx={{ 
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid rgba(0,0,0,0.08)',
              overflow: 'hidden',
              overflowX: 'auto'
            }}>
              <Box sx={{ px: 1.5, pt: 1.25, pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '16px', color: '#37352f', fontWeight: 600, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif' }}>
                    Top Productos
                  </Typography>
                  {!summaryLoading && !summaryError && metricsMonthLabel && (
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {metricsMonthLabel}
                    </Typography>
                  )}
                </Box>
                {/* Subcategory filter */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel id="subcategory-filter-label" sx={{ fontSize: '0.8rem' }}>
                    {t('filter_subcategory', { defaultValue: 'Subcategoría' })}
                  </InputLabel>
                  <Select
                    labelId="subcategory-filter-label"
                    value={subcategoryFilter}
                    label={t('filter_subcategory', { defaultValue: 'Subcategoría' })}
                    onChange={(e) => setSubcategoryFilter(e.target.value)}
                    sx={{ '& .MuiSelect-select': { fontSize: '0.8rem', py: 0.5 } }}
                  >
                    <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                      <em>{t('all_subcategories', { defaultValue: 'Todas' })}</em>
                    </MenuItem>
                    {subcategories.map((subcat) => (
                      <MenuItem key={subcat} value={subcat} sx={{ fontSize: '0.8rem' }}>
                        {subcat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Box sx={{ 
                  px: 1,
                  '& .ant-table': { backgroundColor: 'transparent' },
                  '& .ant-table-thead > tr > th': {
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid #e7e5e4',
                    fontSize: '11px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                    fontWeight: 500,
                    color: '#787774',
                    textTransform: 'none',
                    letterSpacing: '-0.01em',
                    padding: '8px 6px',
                    textAlign: 'center',
                  },
                  '& .ant-table-tbody > tr > td': {
                    borderBottom: '1px solid #f0efee',
                    padding: '8px 6px',
                  },
                  '& .ant-table-tbody > tr:hover > td': {
                    backgroundColor: '#fbfbfa',
                  },
                  // Show AntD pagination controls
                  '& .ant-pagination': { display: 'block' }
                }}>
                  {productsError && (
                    <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                      {t('error')}: {productsError}
                    </Typography>
                  )}
                  <ConfigProvider locale={{ es: esES, en: enUS, it: itIT }[i18n.language] || esES}>
                    <Table
                      columns={[
                        { title: 'Producto', dataIndex: 'name', key: 'name', width: 360, align: 'center',
                          render: (text) => (
                            <Typography sx={{ fontSize: '12px', color: '#2563eb', fontWeight: 600 }} title={text}>
                              {text}
                            </Typography>
                          )
                        },
                        { title: 'Categoría', dataIndex: 'productCategory', key: 'productCategory', width: 120, align: 'center',
                          render: (text) => (
                            <Typography sx={{ fontSize: '11px', color: '#6b7280' }} title={text}>
                              {text}
                            </Typography>
                          )
                        },
                        { title: 'Subcategoría', dataIndex: 'subcategory', key: 'subcategory', width: 130, align: 'center',
                          render: (text) => (
                            <Typography sx={{ fontSize: '11px', color: '#6b7280' }} title={text}>
                              {text || '—'}
                            </Typography>
                          )
                        },
                        {
                          title: (
                            <Box
                              onClick={() => handleColumnSort('units')}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: '#4f46e5' }
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>Unidades</Typography>
                              {sortBy === 'units' ? (
                                sortOrder === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} /> : <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                              ) : (
                                <SwapVertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                              )}
                            </Box>
                          ),
                          dataIndex: 'totalUnits', key: 'totalUnits', width: 95, align: 'center',
                          render: (val) => (
                            <Typography sx={{ fontSize: '11px', color: '#1a1a1a' }}>
                              {new Intl.NumberFormat().format(Number(val||0))}
                            </Typography>
                          )
                        },
                        {
                          title: (
                            <Box
                              onClick={() => handleColumnSort('revenue')}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: '#4f46e5' }
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>Ingresos</Typography>
                              {sortBy === 'revenue' ? (
                                sortOrder === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} /> : <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                              ) : (
                                <SwapVertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                              )}
                            </Box>
                          ),
                          dataIndex: 'totalRevenue', key: 'totalRevenue', width: 105, align: 'center',
                          render: (val) => (
                            <Typography sx={{ fontSize: '11px', color: '#15803d', fontWeight: 800 }}>
                              {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(val||0))}
                            </Typography>
                          )
                        },
                        {
                          title: (
                            <Box
                              onClick={() => handleColumnSort('profit')}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: '#4f46e5' }
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>Beneficio</Typography>
                              {sortBy === 'profit' ? (
                                sortOrder === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} /> : <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                              ) : (
                                <SwapVertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                              )}
                            </Box>
                          ),
                          dataIndex: 'totalProfit', key: 'totalProfit', width: 105, align: 'center',
                          render: (val) => (
                            <Typography sx={{ fontSize: '11px', color: '#1d4ed8', fontWeight: 800 }}>
                              {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' }).format(Number(val||0))}
                            </Typography>
                          )
                        },
                        {
                          title: (
                            <Box
                              onClick={() => handleColumnSort('price')}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: '#4f46e5' }
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>Precio</Typography>
                              {sortBy === 'price' ? (
                                sortOrder === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} /> : <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                              ) : (
                                <SwapVertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                              )}
                            </Box>
                          ),
                          dataIndex: 'avgPrice', key: 'avgPrice', width: 85, align: 'center',
                          render: (val) => (
                            <Typography sx={{ fontSize: '11px', color: '#1a1a1a' }}>
                              {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(val||0))}
                            </Typography>
                          )
                        },
                        {
                          title: (
                            <Box
                              onClick={() => handleColumnSort('cost')}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: '#4f46e5' }
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>Coste</Typography>
                              {sortBy === 'cost' ? (
                                sortOrder === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} /> : <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                              ) : (
                                <SwapVertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                              )}
                            </Box>
                          ),
                          dataIndex: 'avgCost', key: 'avgCost', width: 85, align: 'center',
                          render: (val) => (
                            <Typography sx={{ fontSize: '11px', color: '#1a1a1a' }}>
                              {new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(Number(val||0))}
                            </Typography>
                          )
                        },
                        {
                          title: (
                            <Box
                              onClick={() => handleColumnSort('share')}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.25,
                                cursor: 'pointer',
                                userSelect: 'none',
                                '&:hover': { color: '#4f46e5' }
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500 }}>% Ventas</Typography>
                              {sortBy === 'share' ? (
                                sortOrder === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} /> : <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4f46e5' }} />
                              ) : (
                                <SwapVertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                              )}
                            </Box>
                          ),
                          dataIndex: 'salesSharePct', key: 'salesSharePct', width: 95, align: 'center',
                          render: (val) => (
                            <Typography sx={{ fontSize: '11px', color: '#1a1a1a', fontWeight: 700 }}>
                              {(Number(val||0)).toFixed(1)}%
                            </Typography>
                          )
                        },
                        { 
                          title: <AccessTimeOutlinedIcon sx={{ color: '#787774' }} fontSize="small" />, 
                          key: 'peakHours', 
                          width: 90,
                          align: 'center',
                          render: (_, record) => (
                            <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'center' }}>
                              {[...(record.peakHours || [])]
                                .map(h => String(h))
                                .sort((a, b) => parseInt(a.split('-')[0]) - parseInt(b.split('-')[0]))
                                .slice(0, 2)
                                .map((h, idx) => (
                                <Chip
                                  clickable={false}
                                  key={idx}
                                  label={h}
                                  size="small"
                                  sx={{
                                    height: '18px',
                                    fontSize: '10px',
                                    color: '#6d28d9',
                                    backgroundColor: 'rgba(109, 40, 217, 0.12)',
                                    borderColor: 'rgba(109, 40, 217, 0.25)'
                                  }}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )
                        },
                        { title: 'Días', key: 'topDays', width: 140, align: 'center',
                          render: (_, record) => {
                            const order = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];
                            const days = (record.topDays || [])
                              .slice(0, 3)
                              .sort((a, b) => order.indexOf(String(a)) - order.indexOf(String(b)));
                            return (
                              <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'center' }}>
                                {days.map((d, idx) => (
                                  <Chip
                                    clickable={false}
                                    key={idx}
                                    label={String(d)}
                                    size="small"
                                    sx={{
                                      height: '18px',
                                      fontSize: '10px',
                                      color: '#6b7280',
                                      backgroundColor: 'rgba(0,0,0,0.04)',
                                      borderColor: 'rgba(0,0,0,0.08)'
                                    }}
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            );
                          }
                        },
                        { title: 'Comprado juntos', key: 'copurchases', width: 220, align: 'center',
                          render: (_, record) => (
                            <Box sx={{ display: 'flex', gap: 0.5, whiteSpace: 'nowrap', overflow: 'hidden', justifyContent: 'center' }}>
                              {(record.topCopurchases || []).filter(c => !!c.productName).slice(0, 2).map((c, idx) => (
                                <Chip
                                  clickable={false}
                                  key={idx}
                                  label={c.productName}
                                  size="small"
                                  sx={{
                                    height: '18px',
                                    fontSize: '10px',
                                    color: '#37352f',
                                    backgroundColor: '#f9f7f4',
                                    borderColor: 'rgba(0,0,0,0.08)'
                                  }}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          )
                        },
                        ...((analysisMode === 'machine' || analysisMode === 'cluster' || analysisMode === 'sector' || analysisMode === 'route')
                          ? [{
                              title: (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                                  <Typography component="span" sx={{ fontSize: '11px', color: '#787774', fontWeight: 500 }}>
                                    {t('ai_action', { defaultValue: 'AI Action' })}
                                  </Typography>
                                  <MuiTooltip
                                    title={
                                      problemSortDirection === 'desc'
                                        ? t('problems_first', { defaultValue: 'Problemas destacados arriba' })
                                        : problemSortDirection === 'asc'
                                          ? t('problems_last', { defaultValue: 'Problemas al final' })
                                          : t('toggle_problem_sort', { defaultValue: 'Ordenar por problemas' })
                                    }
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={handleToggleProblemSort}
                                      sx={{
                                        p: 0.25,
                                        color: problemSortDirection ? '#f97316' : '#9ca3af',
                                        '&:hover': {
                                          color: problemSortDirection === 'asc' ? '#2563eb' : '#ea580c'
                                        }
                                      }}
                                    >
                                      {problemSortDirection === 'desc' ? (
                                        <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                                      ) : problemSortDirection === 'asc' ? (
                                        <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                                      ) : (
                                        <SwapVertIcon sx={{ fontSize: 16 }} />
                                      )}
                                    </IconButton>
                                  </MuiTooltip>
                                </Box>
                              ),
                              key: 'aiAction',
                              width: 90,
                              align: 'center',
                              render: (_, record) => {
                                if (!record.aiAction || !isCurrentSelectedPeriod) {
                                  return (
                                    <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                                      —
                                    </Typography>
                                  );
                                }
                                const isAggregatedView = analysisMode === 'cluster' || analysisMode === 'sector' || analysisMode === 'route';

                                // For machine mode: calculate combined count of problems + alerts
                                // aiAction structure: { problem, experiment, alerts: [...] }
                                const alerts = record.aiAction?.alerts || [];
                                const hasProblem = Boolean(record.aiAction?.problem || record.aiAction?.experiment);
                                const totalCount = (hasProblem ? 1 : 0) + alerts.length;

                                // For aggregated views: use problemCount and alertCount from backend
                                const problemCount = record.aiAction?.problemCount || 0;
                                const alertCount = record.aiAction?.alertCount || 0;
                                const hasProblemAggregated = problemCount > 0;
                                const hasAlertAggregated = alertCount > 0;

                                // Skip if no items to show (for machine mode with new structure)
                                if (!isAggregatedView && totalCount === 0) {
                                  return (
                                    <Typography sx={{ fontSize: '11px', color: '#9ca3af' }}>
                                      —
                                    </Typography>
                                  );
                                }

                                // For aggregated views: show dual icons when both exist
                                if (isAggregatedView) {
                                  return (
                                    <MuiTooltip title={t('open_ai_diagnostics', { defaultValue: 'Ver diagnóstico IA' })}>
                                      <Box
                                        onClick={(event) => handleOpenClusterProblemsModal(event, record)}
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: 'pointer',
                                          gap: 0.5,
                                        }}
                                      >
                                        {/* Problems indicator - Red */}
                                        {hasProblemAggregated && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#ef4444',
                                              '&:hover': { color: '#dc2626' }
                                            }}
                                          >
                                            <WarningAmberIcon sx={{ fontSize: 16 }} />
                                            <Typography
                                              component="span"
                                              sx={{ fontSize: '10px', fontWeight: 600, ml: 0.25, color: 'inherit' }}
                                            >
                                              ({problemCount})
                                            </Typography>
                                          </Box>
                                        )}
                                        {/* Alerts indicator - Purple */}
                                        {hasAlertAggregated && (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              color: '#a855f7',
                                              '&:hover': { color: '#9333ea' }
                                            }}
                                          >
                                            <NotificationsActiveIcon sx={{ fontSize: 16 }} />
                                            <Typography
                                              component="span"
                                              sx={{ fontSize: '10px', fontWeight: 600, ml: 0.25, color: 'inherit' }}
                                            >
                                              ({alertCount})
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </MuiTooltip>
                                  );
                                }

                                // For machine mode: single icon with color priority
                                const iconColor = hasProblem ? '#ef4444' : '#a855f7';
                                const hoverColor = hasProblem ? '#dc2626' : '#9333ea';

                              return (
                                  <MuiTooltip title={t('open_ai_diagnostics', { defaultValue: 'Ver diagnóstico IA' })}>
                                    <Box
                                      onClick={(event) => handleOpenProductAiAction(event, record)}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        position: 'relative',
                                        color: iconColor,
                                        '&:hover': {
                                          color: hoverColor
                                        }
                                      }}
                                    >
                                      <WarningAmberIcon sx={{ fontSize: 18 }} />
                                      {/* For machine mode: show badge when count > 1 */}
                                      {totalCount > 1 && (
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: -6,
                                            right: -8,
                                            backgroundColor: iconColor,
                                            color: 'white',
                                            borderRadius: '50%',
                                            minWidth: 16,
                                            height: 16,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 9,
                                            fontWeight: 700,
                                            px: 0.25,
                                          }}
                                        >
                                          {totalCount}
                                        </Box>
                                      )}
                                    </Box>
                                  </MuiTooltip>
                                );
                              }
                            }]
                          : [])
                      ]}
                      dataSource={products}
                      rowKey={(row) => row.id}
                      loading={productsLoading}
                      locale={{
                        emptyText: (
                          <Empty description={t('posAnalysis_noCurrentWeekData')} />
                        )
                      }}
                      pagination={{ current: page, pageSize, total: totalProducts, showSizeChanger: false, onChange: (p) => setPage(p) }}
                      size="small"
                      scroll={{ x: 1380 }}
                    />
                  </ConfigProvider>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
      <ProductAIActionPopover
        open={Boolean(aiActionAnchorEl && selectedAiActionRow?.aiAction)}
        anchorEl={aiActionAnchorEl}
        onClose={handleCloseProductAiAction}
        aiAction={selectedAiActionRow?.aiAction}
        productName={selectedAiActionRow?.name}
        productId={selectedAiActionRow?.id}
        storeId={selectedMachine}
        clientId={currentUser?.customer_id}
        onUpdated={handleAiActionUpdated}
      />
      <AggregatedExperimentsModal
        open={clusterModalOpen}
        onClose={handleCloseClusterModal}
        aggregationType={analysisMode === 'sector' ? 'sector' : analysisMode === 'route' ? 'route' : 'cluster'}
        aggregationId={analysisMode === 'sector' ? selectedSector : analysisMode === 'route' ? selectedRoute : selectedCluster}
        aggregationName={
          analysisMode === 'sector'
            ? (storeSectors.find(s => s.id === selectedSector)?.name || selectedSector)
            : analysisMode === 'route'
              ? (storeRoutes.find(r => String(r.id) === String(selectedRoute))?.name || selectedRoute)
              : (storeClusters.find(c => c.id === selectedCluster)?.name || selectedCluster)
        }
        productId={clusterModalProduct?.id}
        productName={clusterModalProduct?.name}
        clientId={currentUser?.customer_id}
        onExperimentsUpdated={handleClusterExperimentsUpdated}
        month={analysisMode === 'route' ? selectedMonthNumber : null}
        year={analysisMode === 'route' ? selectedYearNumber : null}
        problemCount={clusterModalProduct?.aiAction?.problemCount || 0}
        alertCount={clusterModalProduct?.aiAction?.alertCount || 0}
      />
    </Box>
  );
};

export default POSAnalysisTab;
